// mds-dojo — synchronous module resolver for the legacy `window.require("id")` /
// `require("id")` pattern (the real SED / circular-dep dodges use it: the module
// is expected to already be loaded/bundled, returned synchronously).
//
// In ESM there is no sync require. But the app's `import.meta.glob({eager:true})`
// (see Typescript/main.ts) eagerly loads + registers every module before any
// runtime code runs, so a synchronous lookup is safe. The codemod rewrites
// `require("id")` / `window.require("id")` → `mdsRequire("id")`.
//
// NOTE: only works for EAGERLY-bundled modules. If a codebase moves to lazy
// (`eager:false`) chunking, sync require sites must be refactored to `await import()`
// (there is no sync path for an unloaded chunk).

const registry: Record<string, any> = {};

/**
 * **mds-dojo** — register an already-loaded module under its dojo id so a later
 * synchronous {@link mdsRequire} lookup can find it. The app's
 * `import.meta.glob({eager:true})` boot (see `Typescript/main.ts`) calls this for
 * every bundled module. NOT a vanilla Dojo 1.x export.
 * @param id the dojo module id, e.g. `"dojo/topic"`.
 * @param mod the module namespace object (its `.default` is unwrapped by {@link mdsRequire}).
 */
export function __register(id: string, mod: any): void {
  registry[id] = mod;
}

/**
 * **mds-dojo** — synchronous module resolver: the ESM stand-in for legacy
 * `require("id")` / `window.require("id")` (the classic SED / circular-dep dodge that
 * expects the module to be already loaded and returned synchronously). The codemod
 * rewrites those call sites to `mdsRequire("id")`. NOT a vanilla Dojo 1.x export.
 *
 * Works ONLY for EAGERLY-bundled modules ({@link __register}ed at boot). There is no
 * sync path for an unloaded lazy chunk — to load a widget **just in time**, use a
 * native dynamic `import()`, which resolves through the same dojo-id aliases:
 * ```ts
 * const Dlg = (await import("moreCore/UI/SelectEntityDialog/SelectEntityDialogLite")).default;
 * const dlg = new Dlg();
 * ```
 *
 * @param id the dojo module id, e.g. `"dojo/topic"` or `"dijit/form/Button"`.
 * @returns the module's `default` export if present, otherwise the module namespace.
 * @throws if `id` was never registered (i.e. not eagerly bundled).
 */
export default function mdsRequire(id: string): any {
  const mod = registry[id];
  if (mod === undefined) {
    throw new Error('mdsRequire: module not registered (not eagerly bundled?): ' + id);
  }
  return mod.default !== undefined ? mod.default : mod;
}
